import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Topbar } from "../components/shell/Topbar";
import { useToast } from "../components/shell/Toast";
import { useLayoutContext } from "../layouts/DashboardLayout";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Input } from "../components/ui/input";
import { cn } from "@/packages/utils/cn";

interface Webhook {
  id: string;
  referenceId: string;
  name: string;
  forwardEnabled: boolean;
  forwardUrl: string | null;
}

interface ForwardResponse {
  status: number;
  headers: Record<string, string>;
  body: string;
  durationMs: number;
  error?: string;
}

interface CapturedRequest {
  id: string;
  method: string;
  headers: Record<string, string>;
  body: unknown;
  query: Record<string, string>;
  ip: string;
  timestamp: string;
  forwardResponse?: ForwardResponse;
  replays?: Array<{
    at: string;
    status: number;
    durationMs: number;
    by: string | null;
    error?: string;
  }>;
}

type Tab = "body" | "headers" | "response" | "replays";

export function WebhookDetailPage() {
  const { teamId, projectId, webhookId } = useParams();
  const [searchParams] = useSearchParams();
  const requestIdQuery = searchParams.get("request");
  const { teams, projects } = useLayoutContext();
  const team = teams.find((t) => t.id === teamId);
  const project = (projects[teamId ?? ""] ?? []).find((p) => p.referenceId === projectId);

  const [webhook, setWebhook] = useState<Webhook | null>(null);
  const [requests, setRequests] = useState<CapturedRequest[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(requestIdQuery);
  const [tab, setTab] = useState<Tab>("body");
  const [replaying, setReplaying] = useState(false);
  const { toast } = useToast();

  const refresh = async () => {
    if (!projectId || !webhookId) return;
    const w = await fetch(
      `/api/projects/${projectId}/webhooks/${webhookId}`,
    ).then((r) => r.json());
    setWebhook(w);
    const reqs = await fetch(
      `/api/projects/${projectId}/webhooks/${webhookId}/requests`,
    ).then((r) => r.json());
    setRequests(reqs);
    if (!selectedId && reqs[0]) setSelectedId(reqs[0].id);
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, webhookId]);

  const selected = requests.find((r) => r.id === selectedId);

  if (!team || !project || !webhook) return null;

  const updateBypass = async (patch: Partial<Webhook>) => {
    const snapshot = webhook;
    setWebhook({ ...webhook, ...patch });
    try {
      const res = await fetch(`/api/projects/${projectId}/webhooks/${webhookId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      });
      if (!res.ok) {
        const err = (await res.json()) as { error?: string };
        throw new Error(err.error ?? `HTTP ${res.status}`);
      }
    } catch (err) {
      setWebhook(snapshot);
      toast({
        tone: "error",
        title: "Update failed",
        description: err instanceof Error ? err.message : String(err),
      });
    }
  };

  const replay = async () => {
    if (!selected) return;
    const requestId = selected.id;
    setReplaying(true);
    try {
      const res = await fetch(
        `/api/projects/${projectId}/webhooks/${webhookId}/requests/${requestId}/replay`,
        { method: "POST" },
      );
      const data = (await res.json()) as
        | { result: ForwardResponse; replays: CapturedRequest["replays"] }
        | { error: string };

      if (!res.ok || "error" in data) {
        toast({
          tone: "error",
          title: "Replay failed",
          description: "error" in data ? data.error : `HTTP ${res.status}`,
        });
        return;
      }

      setRequests((prev) =>
        prev.map((r) =>
          r.id === requestId
            ? { ...r, forwardResponse: data.result, replays: data.replays }
            : r,
        ),
      );

      const r = data.result;
      if (r.error) {
        toast({
          tone: "error",
          title: "Replay failed",
          description: r.error,
        });
      } else {
        const ok = r.status >= 200 && r.status < 300;
        toast({
          tone: ok ? "success" : "error",
          title: ok ? "Replay sent" : "Replay returned error",
          description: `${r.status} · ${r.durationMs}ms`,
        });
      }
    } catch (err) {
      toast({
        tone: "error",
        title: "Replay failed",
        description: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setReplaying(false);
    }
  };

  const tabs: Array<{ id: Tab; label: string }> = [
    { id: "body", label: "Body" },
    { id: "headers", label: "Headers" },
    { id: "response", label: "Response" },
    {
      id: "replays",
      label: `Replays · ${selected?.replays?.length ?? 0}`,
    },
  ];

  return (
    <>
      <Topbar
        title={selected ? `${selected.method} · ${selected.id.slice(0, 8)}` : webhook.name}
        crumbs={[
          { label: team.name, to: `/teams/${team.id}` },
          { label: project.name, to: `/teams/${team.id}/projects/${project.referenceId}` },
          {
            label: "Webhooks",
            to: `/teams/${team.id}/projects/${project.referenceId}/webhooks`,
          },
          { label: webhook.name },
        ]}
        actions={
          <>
            <Link to={`/teams/${teamId}/projects/${projectId}/webhooks`}>
              <Button size="sm" variant="ghost">
                ← inbox
              </Button>
            </Link>
            <Button
              size="sm"
              onClick={replay}
              disabled={
                replaying || !selected || !webhook.forwardEnabled
              }
            >
              {replaying ? "Replaying…" : "Replay"}
            </Button>
          </>
        }
      />

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-[280px_1fr_300px] overflow-hidden">
        {/* Request list */}
        <aside className="border-r border-rule overflow-auto bg-card-paper">
          <div className="px-4 py-3 border-b border-rule text-eyebrow">
            Requests · {requests.length}
          </div>
          {requests.map((r) => (
            <button
              key={r.id}
              onClick={() => setSelectedId(r.id)}
              className={cn(
                "block w-full text-left px-4 py-2.5 border-b border-rule-soft border-l-2",
                r.id === selectedId
                  ? "bg-panel border-l-signal-red"
                  : "border-l-transparent hover:bg-panel/40",
              )}
            >
              <div className="flex items-center gap-2">
                <span className="font-mono text-[10px] font-bold text-signal-ok">
                  {r.method}
                </span>
                <span className="font-mono text-xs flex-1 truncate">
                  {r.id.slice(0, 8)}
                </span>
                {r.forwardResponse && (
                  <span
                    className={cn(
                      "font-mono text-[10px]",
                      r.forwardResponse.status >= 200 &&
                        r.forwardResponse.status < 300
                        ? "text-signal-ok"
                        : "text-signal-red",
                    )}
                  >
                    {r.forwardResponse.status}
                  </span>
                )}
              </div>
              <div className="font-mono text-[10px] text-faint mt-0.5">
                {new Date(r.timestamp).toLocaleTimeString()}
              </div>
            </button>
          ))}
        </aside>

        {/* Detail */}
        <div className="overflow-auto p-6">
          {!selected ? (
            <div className="text-sm text-muted-foreground italic">
              Select a request from the list.
            </div>
          ) : (
            <>
              <div className="flex gap-1 border-b border-rule mb-4">
                {tabs.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => setTab(t.id)}
                    className={cn(
                      "px-3.5 py-2 text-sm border-b-2",
                      tab === t.id
                        ? "border-foreground text-foreground font-semibold"
                        : "border-transparent text-muted-foreground hover:text-foreground",
                    )}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              {tab === "body" && (
                <pre className="bg-card-paper border border-rule rounded-md p-4 font-mono text-xs whitespace-pre-wrap break-all">
                  {typeof selected.body === "string"
                    ? selected.body
                    : JSON.stringify(selected.body, null, 2)}
                </pre>
              )}
              {tab === "headers" && (
                <div className="bg-card-paper border border-rule rounded-md p-4 font-mono text-xs space-y-1">
                  {Object.entries(selected.headers).map(([k, v]) => (
                    <div key={k}>
                      <span className="text-faint">{k}:</span> {v}
                    </div>
                  ))}
                </div>
              )}
              {tab === "response" &&
                (selected.forwardResponse ? (
                  <div className="space-y-3">
                    <div className="font-mono text-xs">
                      Status:{" "}
                      <span
                        className={
                          selected.forwardResponse.status < 300
                            ? "text-signal-ok"
                            : "text-signal-red"
                        }
                      >
                        {selected.forwardResponse.status}
                      </span>{" "}
                      · {selected.forwardResponse.durationMs}ms
                    </div>
                    {selected.forwardResponse.error && (
                      <div className="text-signal-red text-sm">
                        {selected.forwardResponse.error}
                      </div>
                    )}
                    <pre className="bg-card-paper border border-rule rounded-md p-4 font-mono text-xs whitespace-pre-wrap break-all">
                      {selected.forwardResponse.body || "<empty body>"}
                    </pre>
                  </div>
                ) : (
                  <div className="text-sm text-muted-foreground italic">
                    No forward response — bypass was off when this request was
                    captured.
                  </div>
                ))}
              {tab === "replays" &&
                ((selected.replays ?? []).length === 0 ? (
                  <div className="text-sm text-muted-foreground italic">
                    No replays yet.
                  </div>
                ) : (
                  <div className="space-y-1.5 font-mono text-xs">
                    {selected.replays!.map((r, i) => (
                      <div
                        key={i}
                        className="bg-card-paper border border-rule rounded-md px-3 py-2 flex justify-between"
                      >
                        <span>
                          {new Date(r.at).toLocaleString()} · {r.by ?? "system"}
                        </span>
                        <span>
                          {r.status} · {r.durationMs}ms
                        </span>
                      </div>
                    ))}
                  </div>
                ))}
            </>
          )}
        </div>

        {/* Bypass rail */}
        <aside className="border-l border-rule bg-panel/40 p-5 space-y-5 overflow-auto">
          <div>
            <div className="text-eyebrow mb-2">Bypass to local</div>
            <div className="bg-card-paper border border-rule rounded-md p-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold">Forward enabled</span>
                <Switch
                  checked={webhook.forwardEnabled}
                  onCheckedChange={(v) => updateBypass({ forwardEnabled: v })}
                />
              </div>
              <Input
                value={webhook.forwardUrl ?? ""}
                onChange={(e) =>
                  setWebhook({ ...webhook, forwardUrl: e.target.value })
                }
                onBlur={(e) =>
                  updateBypass({ forwardUrl: e.target.value || null })
                }
                placeholder="https://localhost:3000/sink"
                className="font-mono text-xs"
              />
            </div>
          </div>
          <div>
            <div className="text-eyebrow mb-2">Replays · {selected?.replays?.length ?? 0}</div>
            <div className="space-y-1 font-mono text-xs text-muted-foreground">
              {(selected?.replays ?? []).slice(0, 5).map((r, i) => (
                <div key={i} className="border-b border-rule-soft py-1.5">
                  {new Date(r.at).toLocaleString()} · {r.status} OK · by{" "}
                  {r.by ?? "system"}
                </div>
              ))}
            </div>
          </div>
        </aside>
      </div>
    </>
  );
}
